import type { ScanFormat, ScanPayload } from "../../shared/scanTypes";
import { parseLabelUuid } from "./label";

/**
 * The classified content of a decoded or typed code. The shape lives in
 * `shared/scanTypes` so the scan-history store (also shared) can type it
 * without importing this feature module; re-exported here as the scan
 * feature's public API.
 */
export type { ScanPayload } from "../../shared/scanTypes";

/**
 * The payloads that get an honest, action-bearing view instead of a label
 * chooser or a product lookup: a URL, a Wi-Fi network or plain text
 * (results-06).
 */
export type DisplayPayload = Extract<ScanPayload, { kind: "url" | "wifi" | "text" }>;

const ONE_D_FORMATS: ReadonlySet<ScanFormat> = new Set([
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "code_128",
  "code_39",
]);

/** A manual product code: EAN-8, UPC-A, EAN-13 or GTIN-14 (check digit unchecked). */
const PRODUCT_CODE_RE = /^(?:\d{8}|\d{12,14})$/;

/** Cap for every stored/displayed payload (scan-results-history spec). */
export const MAX_PAYLOAD_LENGTH = 1024;

/**
 * Classify a decoded or typed code into a scan payload.
 *
 * The cap is applied before classification so every kind-specific field is
 * bounded too, not just `raw`. A payload longer than the cap is classified by
 * its stored prefix and flagged as truncated.
 *
 * @param text the decoded or typed payload.
 * @param format the normalized symbology, or `manual` for typed entry.
 * @returns the payload kind plus the capped raw text.
 */
export function classifyScan(text: string, format: ScanFormat): ScanPayload {
  const { raw, truncated } = capPayload(text);
  const trimmed = raw.trim();
  // A 1D symbology is a product barcode even when the payload looks like a
  // UUID label or when no provider knows the code.
  if (ONE_D_FORMATS.has(format)) {
    return { kind: "product", code: trimmed, raw, truncated };
  }
  const uuid = parseLabelUuid(trimmed);
  if (uuid !== null) return { kind: "label", uuid, raw, truncated };
  // Typed entry (and a symbology we could not name) falls back to the payload
  // shape: 8/12/13/14 digits are product codes, not label UUIDs.
  if ((format === "manual" || format === "unknown") && PRODUCT_CODE_RE.test(trimmed)) {
    return { kind: "product", code: trimmed, raw, truncated };
  }
  if (isOpenableUrl(trimmed)) {
    return { kind: "url", href: trimmed, raw, truncated };
  }
  const wifi = parseWifi(trimmed);
  if (wifi !== null) return { kind: "wifi", ...wifi, raw, truncated };
  return { kind: "text", text: trimmed, raw, truncated };
}

/**
 * Cap the payload at 1024 characters without splitting a surrogate pair.
 *
 * @param text the exact decoded/typed text.
 * @returns the stored prefix and whether anything was cut.
 */
function capPayload(text: string): { raw: string; truncated: boolean } {
  // Fast path: UTF-16 units cannot be fewer than code points, so a short string
  // is always below the cap.
  if (text.length <= MAX_PAYLOAD_LENGTH) return { raw: text, truncated: false };
  const characters = Array.from(text);
  if (characters.length <= MAX_PAYLOAD_LENGTH) return { raw: text, truncated: false };
  return {
    raw: characters.slice(0, MAX_PAYLOAD_LENGTH).join(""),
    truncated: true,
  };
}

/**
 * Whether a payload is an openable web URL. Only `http:` and `https:` pass, so
 * `javascript:`, `data:`, `file:` and custom schemes can never become links.
 * Exported so the open confirmation re-checks the destination before handing
 * it to the system browser (results-06).
 *
 * @param value the raw payload or a destination about to be opened.
 * @returns true only for an absolute `http(s)` URL.
 */
export function isOpenableUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/** A canonical UUID anywhere in the text (URN-, brace- or URL-wrapped). */
const UUID_IN_TEXT_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Whether a non-label payload looks like a label attempt that failed plain-UUID
 * validation: `urn:uuid:…`, a brace-wrapped UUID, a URL containing a UUID. The
 * unsupported-label explanation is reserved for these payloads (results-06);
 * an ordinary URL/text/Wi-Fi payload is not a failed label.
 *
 * @param text the non-label payload text.
 * @returns true when a canonical UUID appears inside the text.
 */
export function looksLikeLabelAttempt(text: string): boolean {
  return UUID_IN_TEXT_RE.test(text);
}

interface WifiDetails {
  ssid: string;
  security?: string;
  password?: string;
  hidden: boolean;
}

const WIFI_FIELDS: ReadonlySet<string> = new Set(["T", "S", "P", "H"]);

/**
 * Parse the `WIFI:T:<auth>;S:<ssid>;P:<password>;H:<hidden>;;` payload shape.
 *
 * @returns the Wi-Fi details, or null when the payload is malformed and must
 *   degrade to plain text.
 */
function parseWifi(value: string): WifiDetails | null {
  if (value.slice(0, 5).toUpperCase() !== "WIFI:") return null;
  const body = value.slice(5);
  // A field list that is not terminated by `;` is truncated or malformed.
  if (!body.endsWith(";")) return null;
  const fields = new Map<string, string>();
  for (const field of splitWifiFields(body)) {
    if (field === "") continue;
    const colon = field.indexOf(":");
    if (colon <= 0) return null;
    const key = field.slice(0, colon).toUpperCase();
    if (!WIFI_FIELDS.has(key)) return null;
    fields.set(key, unescapeWifi(field.slice(colon + 1)));
  }
  const ssid = fields.get("S");
  if (!ssid) return null;
  const security = fields.get("T");
  const password = fields.get("P");
  return {
    ssid,
    security: security || undefined,
    password: password || undefined,
    hidden: (fields.get("H") ?? "").toLowerCase() === "true",
  };
}

/** Split on unescaped `;`, keeping escapes intact for `unescapeWifi`. */
function splitWifiFields(body: string): string[] {
  const fields: string[] = [];
  let current = "";
  let escaped = false;
  for (const char of body) {
    if (escaped) {
      current += `\\${char}`;
      escaped = false;
    } else if (char === "\\") {
      escaped = true;
    } else if (char === ";") {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (escaped) current += "\\";
  fields.push(current);
  return fields;
}

/** Resolve the Wi-Fi payload escapes (`\;`, `\:`, `\,`, `\\`). */
function unescapeWifi(value: string): string {
  let result = "";
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    if (char === "\\" && i + 1 < value.length) {
      result += value.charAt(i + 1);
      i += 1;
    } else {
      result += char;
    }
  }
  return result;
}
